// Distribution / Этап 4 — модалка распределения томов ПД по специалистам.
// Источник: POST /projects/{id}/distribute (см. app/assignment/distributor.py).
// Структура: 3 цвета карточек (proposed/pending_invite/unassigned+needs_external).
import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, AlertCircle, UserX, Send, Users, UserPlus } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { api } from '@/api/client';
import { toast } from '@/state/toast';

interface Props {
  projectId: string;
  onClose: () => void;
}

interface Assignment {
  expert_id: string | null;
  expert_name: string | null;
  specialty: string;
  specialty_title?: string;
  role: 'primary' | 'secondary' | 'consultant';
  status: 'proposed' | 'pending_invite' | 'unassigned' | 'approved';
  needs_external?: boolean;
  confidence?: string;
  rationale?: string;
  expert_workload?: string | null;
}

interface VolumeRow {
  document_id: string;
  volume_title: string;
  volume_code: string | null;
  pp87_section_code: string | null;
  assignments: Assignment[];
}

interface DistributeResponse {
  project_id: string;
  project_kind: string | null;
  total_volumes: number;
  volumes_with_primary: number;
  summary: {
    proposed: number;
    pending_invite: number;
    unassigned: number;
    needs_external: number;
    active_experts: number;
    total_volumes: number;
  };
  save_result: { inserted: number; skipped: number };
  rows: VolumeRow[];
}

type StatusKind = 'proposed' | 'pending_invite' | 'needs_external' | 'unassigned';

function classifyAssignment(a: Assignment): StatusKind {
  if (a.needs_external) return 'needs_external';
  if (a.status === 'pending_invite') return 'pending_invite';
  if (a.status === 'unassigned') return 'unassigned';
  return 'proposed';
}

const STATUS_META: Record<StatusKind, { label: string; color: string; bg: string; Icon: typeof CheckCircle2 }> = {
  proposed:        { label: 'Распределено',  color: '#16A34A', bg: '#DCFCE7', Icon: CheckCircle2 },
  pending_invite:  { label: 'Без аккаунта',  color: '#CA8A04', bg: '#FEF9C3', Icon: Send },
  needs_external:  { label: 'Нет в команде', color: '#DC2626', bg: '#FEE2E2', Icon: UserX },
  unassigned:      { label: 'Не определена', color: '#64748B', bg: '#F1F5F9', Icon: AlertCircle },
};

interface ColumnData {
  key: string;
  expert_id: string | null;
  expert_name: string | null;
  specialty: string;
  specialty_title: string;
  status_kind: StatusKind;
  workload: string | null;
  rows: VolumeRow[];
}

function projectKindRu(kind: string | null | undefined): string {
  if (kind === 'linear') return 'Линейный';
  if (kind === 'areal') return 'Площадной';
  if (kind === 'mixed') return 'Смешанный';
  return kind || '—';
}

interface ActionDialogState {
  action: 'invite' | 'attach';
  specialty: string;
  specialtyTitle: string;
}

export function DistributeDocumentsModal({ projectId, onClose }: Props) {
  const qc = useQueryClient();
  const [force, setForce] = useState(false);
  const [actionDialog, setActionDialog] = useState<ActionDialogState | null>(null);

  const distributeQ = useQuery<DistributeResponse>({
    queryKey: ['distribute', projectId, force],
    queryFn: () =>
      api.post<DistributeResponse>(
        `/projects/${projectId}/distribute${force ? '?force=true' : ''}`,
        {},
      ),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const approve = useMutation({
    mutationFn: () => api.post<{ approved_count: number }>(`/projects/${projectId}/distribute/approve`, {}),
    onSuccess: (r) => {
      toast.success(`Подтверждено ${r.approved_count} назначений`);
      qc.invalidateQueries({ queryKey: ['assignments', projectId] });
      onClose();
    },
    onError: (e: unknown) => toast.error(`Ошибка: ${(e as Error)?.message ?? 'unknown'}`),
  });

  const data = distributeQ.data;

  // Группируем по (имя_эксперта + специальность) — один и тот же эксперт может
  // получить тома по разным специальностям, и это должно быть видно как
  // отдельные колонки.
  const grouped = useMemo(() => {
    if (!data) return [] as ColumnData[];
    const buckets = new Map<string, ColumnData>();
    for (const row of data.rows) {
      const primary = row.assignments[0];
      if (!primary) continue;
      const expertKey = primary.expert_id || '__none__';
      const specKey = primary.specialty || '__none__';
      const key = `${expertKey}|${specKey}`;
      let col = buckets.get(key);
      if (!col) {
        col = {
          key,
          expert_id: primary.expert_id,
          expert_name: primary.expert_name,
          specialty: primary.specialty,
          specialty_title: primary.specialty_title || primary.specialty,
          status_kind: classifyAssignment(primary),
          workload: primary.expert_workload ?? null,
          rows: [],
        };
        buckets.set(key, col);
      }
      col.rows.push(row);
    }
    // Сортировка: сначала proposed, затем pending_invite, потом без эксперта
    const order: Record<StatusKind, number> = { proposed: 0, pending_invite: 1, needs_external: 2, unassigned: 3 };
    return Array.from(buckets.values()).sort((a, b) =>
      (order[a.status_kind] - order[b.status_kind]) || a.specialty_title.localeCompare(b.specialty_title, 'ru'),
    );
  }, [data]);

  return (
    <Modal title="Распределение томов ПД" onClose={onClose} maxWidth={1200}>
      <div style={{ padding: 4 }}>
        {distributeQ.isLoading && (
          <div style={{ padding: 24, textAlign: 'center', color: '#64748B' }}>
            Анализируем структуру проекта и подбираем экспертов… (~15 сек)
          </div>
        )}
        {distributeQ.isError && (
          <div style={{ padding: 16, background: '#FEE2E2', borderRadius: 8, color: '#991B1B' }}>
            Ошибка: {(distributeQ.error as Error)?.message ?? 'unknown'}
          </div>
        )}
        {data && (
          <>
            {/* Сводка */}
            <SummaryBar data={data} />

            {/* Колонки: одна колонка = (эксперт + специальность) */}
            <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
              {grouped.map(col => (
                <SpecialtyColumn key={col.key} col={col} onAction={(action, specialty, specialtyTitle) => setActionDialog({ action, specialty, specialtyTitle })} />
              ))}
            </div>

            {actionDialog && (
              <AddExpertForSpecialtyDialog
                action={actionDialog.action}
                specialty={actionDialog.specialty}
                specialtyTitle={actionDialog.specialtyTitle}
                onClose={() => setActionDialog(null)}
                onCreated={() => { setActionDialog(null); setForce(true); distributeQ.refetch(); qc.invalidateQueries({ queryKey: ['experts'] }); }}
              />
            )}

            {/* Действия */}
            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <Button variant="ghost" size="sm" onClick={() => { setForce(true); distributeQ.refetch(); }}>
                Пересчитать ↻
              </Button>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="ghost" onClick={onClose}>Отмена</Button>
                <Button
                  variant="primary"
                  onClick={() => approve.mutate()}
                  disabled={approve.isPending || data.summary.proposed === 0}
                >
                  {approve.isPending ? 'Подтверждаем…' : `Подтвердить (${data.summary.proposed})`}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function SummaryBar({ data }: { data: DistributeResponse }) {
  const s = data.summary;
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
      <Pill icon={Users} label={`Экспертов в команде: ${s.active_experts}`} color="#475569" />
      <Pill icon={CheckCircle2} label={`Распределено: ${s.proposed}`} color={STATUS_META.proposed.color} bg={STATUS_META.proposed.bg} />
      <Pill icon={Send} label={`Без аккаунта: ${s.pending_invite}`} color={STATUS_META.pending_invite.color} bg={STATUS_META.pending_invite.bg} />
      <Pill icon={UserX} label={`Нет в команде: ${s.needs_external}`} color={STATUS_META.needs_external.color} bg={STATUS_META.needs_external.bg} />
      <Pill icon={AlertCircle} label={`Не определена: ${s.unassigned - s.needs_external}`} color={STATUS_META.unassigned.color} bg={STATUS_META.unassigned.bg} />
      <div style={{ marginLeft: 'auto', fontSize: 13, color: '#475569' }}>
        Тип объекта: <b>{projectKindRu(data.project_kind)}</b> · Томов: <b>{data.total_volumes}</b>
      </div>
    </div>
  );
}

function Pill({ icon: Icon, label, color, bg }: { icon: typeof Users; label: string; color: string; bg?: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999,
      background: bg ?? '#F1F5F9', color, fontSize: 12, fontWeight: 500,
    }}>
      <Icon size={14} />
      {label}
    </span>
  );
}

function SpecialtyColumn({ col, onAction }: { col: ColumnData; onAction: (a: 'invite' | 'attach', specialty: string, specialtyTitle: string) => void }) {
  const meta = STATUS_META[col.status_kind];
  const isNoExpert = !col.expert_id;
  const needsExternal = col.status_kind === 'needs_external';

  return (
    <div style={{
      border: `1px solid ${meta.color}40`,
      borderRadius: 12,
      background: 'white',
      overflow: 'hidden',
    }}>
      {/* Шапка колонки: специальность (крупно) + эксперт (под ней) */}
      <div style={{
        padding: '12px',
        background: meta.bg,
        borderBottom: `1px solid ${meta.color}30`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <meta.Icon size={14} color={meta.color} />
          <span style={{ fontSize: 11, fontWeight: 600, color: meta.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {meta.label}
          </span>
        </div>
        <div title={`Код специальности: ${col.specialty}`} style={{ fontSize: 14, fontWeight: 700, color: '#0F172A', marginBottom: 4 }}>
          {col.specialty_title}
        </div>
        {!needsExternal && (
          <div style={{ fontSize: 12, color: '#475569' }}>
            {isNoExpert ? (
              <span style={{ fontStyle: 'italic' }}>Эксперт не назначен</span>
            ) : (
              <>
                <span style={{ fontWeight: 500 }}>{col.expert_name}</span>
                {col.workload && <span style={{ color: '#94A3B8' }}> · {col.workload}</span>}
              </>
            )}
          </div>
        )}
        {needsExternal && (
          <>
            <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
              В команде нет специалиста этой категории
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                onClick={() => onAction('invite', col.specialty, col.specialty_title)}
                style={actionBtnStyle(meta.color, true)}
              >
                <Send size={12} /> Пригласить по email
              </button>
              <button
                onClick={() => onAction('attach', col.specialty, col.specialty_title)}
                style={actionBtnStyle(meta.color, false)}
              >
                <UserPlus size={12} /> Добавить из команды
              </button>
            </div>
          </>
        )}
      </div>

      {/* Карточки томов */}
      <div style={{ padding: 8 }}>
        {col.rows.map(row => (
          <div key={row.document_id} style={{
            padding: '8px 10px',
            margin: '4px 0',
            background: '#F8FAFC',
            borderRadius: 6,
            fontSize: 12,
            border: '1px solid #F1F5F9',
          }}>
            <div style={{ fontWeight: 500, color: '#0F172A' }}>{row.volume_title}</div>
            {row.pp87_section_code && (
              <div style={{ marginTop: 4 }}>
                <span style={{
                  fontSize: 10, padding: '1px 6px', borderRadius: 4,
                  background: '#E0E7FF', color: '#4338CA', fontWeight: 500,
                }}>
                  {row.pp87_section_code}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function actionBtnStyle(color: string, primary: boolean): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '4px 8px', borderRadius: 6,
    border: `1px solid ${color}`,
    background: primary ? color : 'white',
    color: primary ? 'white' : color,
    fontSize: 11, fontWeight: 500, cursor: 'pointer',
  };
}

function AddExpertForSpecialtyDialog({
  action, specialty, specialtyTitle, onClose, onCreated,
}: {
  action: 'invite' | 'attach';
  specialty: string;
  specialtyTitle: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  // 'invite' = создать нового эксперта (с email или без) + опционально invite
  // 'attach' = выбрать существующего эксперта и добавить ему специальность

  const existingQ = useQuery<{ id: string; full_name: string; specialties: string[]; has_account: boolean }[]>({
    queryKey: ['experts-all'],
    queryFn: () => api.get('/experts?active_only=false'),
  });

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedExistingId, setSelectedExistingId] = useState<string | null>(null);

  const createInvite = useMutation({
    mutationFn: async () => {
      // 1. Создаём эксперта
      const exp = await api.post<{ id: string; email: string | null; has_account: boolean }>('/experts', {
        full_name: fullName.trim(),
        email: email.trim() || null,
        specialties: [specialty],
      });
      // 2. Если есть email — отправляем приглашение
      if (email.trim()) {
        const inv = await api.post<{ token: string; invite_url: string }>(`/experts/${exp.id}/invite`, {});
        const url = inv.invite_url || `${window.location.origin}/invite/${inv.token}`;
        navigator.clipboard?.writeText(url).catch(() => {});
        toast.success('Эксперт добавлен. Ссылка приглашения скопирована в буфер.');
      } else {
        toast.success('Эксперт добавлен в справочник без аккаунта.');
      }
      return exp;
    },
    onSuccess: onCreated,
    onError: (e) => toast.error(`Ошибка: ${(e as Error).message}`),
  });

  const attachToExisting = useMutation({
    mutationFn: async () => {
      if (!selectedExistingId) throw new Error('Не выбран эксперт');
      const existing = existingQ.data?.find(x => x.id === selectedExistingId);
      if (!existing) throw new Error('Эксперт не найден');
      const newSpecialties = Array.from(new Set([...existing.specialties, specialty]));
      await api.patch(`/experts/${selectedExistingId}`, { specialties: newSpecialties });
      toast.success(`Специальность «${specialtyTitle}» добавлена эксперту ${existing.full_name}`);
    },
    onSuccess: onCreated,
    onError: (e) => toast.error(`Ошибка: ${(e as Error).message}`),
  });

  const title = action === 'invite'
    ? `Пригласить специалиста: ${specialtyTitle}`
    : `Добавить из команды: ${specialtyTitle}`;

  return (
    <Modal title={title} onClose={onClose} maxWidth={520}>
      <div style={{ padding: 4, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {action === 'invite' && (
          <>
            <div style={{ fontSize: 13, color: '#475569' }}>
              Создаст нового эксперта в справочнике. Если укажете email — система сгенерирует ссылку-приглашение, по которой эксперт зарегистрируется и получит доступ к своим томам.
            </div>
            <Field label="ФИО эксперта *">
              <input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Иванов Иван Иванович" style={inputStyle} />
            </Field>
            <Field label="Email (для приглашения)">
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="expert@example.com (можно пустым)" style={inputStyle} />
            </Field>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="ghost" onClick={onClose}>Отмена</Button>
              <Button variant="primary" onClick={() => createInvite.mutate()} disabled={!fullName.trim() || createInvite.isPending}>
                {createInvite.isPending ? 'Создаём…' : (email.trim() ? 'Создать и пригласить' : 'Создать без аккаунта')}
              </Button>
            </div>
          </>
        )}

        {action === 'attach' && (
          <>
            <div style={{ fontSize: 13, color: '#475569' }}>
              Выберите эксперта, которому добавить специальность «{specialtyTitle}». После этого распределение пересчитается, и его карточка попадёт в эту колонку.
            </div>
            {existingQ.data && existingQ.data.length === 0 && (
              <div style={{ padding: 12, background: '#FEF9C3', borderRadius: 6, fontSize: 13, color: '#854D0E' }}>
                В справочнике нет экспертов. Воспользуйтесь «Пригласить по email» либо добавьте через Настройки → Эксперты команды.
              </div>
            )}
            {existingQ.data && existingQ.data.length > 0 && (
              <Field label="Эксперт">
                <select value={selectedExistingId ?? ''} onChange={e => setSelectedExistingId(e.target.value || null)} style={inputStyle}>
                  <option value="">— выбрать —</option>
                  {existingQ.data.map(e => (
                    <option key={e.id} value={e.id}>
                      {e.full_name} {e.has_account ? '' : ' (без аккаунта)'}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="ghost" onClick={onClose}>Отмена</Button>
              <Button variant="primary" onClick={() => attachToExisting.mutate()} disabled={!selectedExistingId || attachToExisting.isPending}>
                Добавить специальность
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4, fontWeight: 500 }}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  border: '1px solid #CBD5E1',
  borderRadius: 6,
  fontSize: 13,
};
